import { captureEvent, useCaptureEvent } from "@Light/app/analytics";
import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import type { Utility } from "@Light/services/lightTypes";
import { useState } from "react";
import { EnrollBottomSheet } from "../common/EnrollBottomSheet";
import { EnrollHelpButton } from "../EnrollHelpButtonFooter";
import { UtilityRadioGroup } from "./UtilityRadioGroup";

export type ZipUtilitySplitBottomSheetProps = {
  open: boolean;
  onClose: () => void;
  postalCode?: string;
  utilities: Utility[];
  onSubmit: (utility: Utility) => void;
  onClickProvideAddress: () => void;
};

export const ZipUtilitySplitBottomSheet = ({
  open,
  onClose,
  postalCode,
  utilities,
  onSubmit,
  onClickProvideAddress,
}: ZipUtilitySplitBottomSheetProps) => {
  const [selected, setSelected] = useState<Utility>();

  useCaptureEvent({
    event: "enroll_zip_split_viewed",
    condition: open,
    properties: {
      variant: "sheet",
      postal_code: postalCode ?? null,
      utility_count: utilities.length,
    },
  });

  return (
    <EnrollBottomSheet
      open={open}
      onClose={() => {
        captureEvent("enroll_zip_split_dismissed", {
          variant: "sheet",
          postal_code: postalCode ?? null,
        });
        setSelected(undefined);
        onClose();
      }}
      title="Who is your local utility provider?"
      subtitle={`${postalCode ? `Zip code ${postalCode}` : "Your zip code"} is split between utility providers. We need your local utility for accurate pricing.`}
      data-testid="zip-utility-split-sheet"
      footer={
        <ButtonFooter>
          <Button
            size="lg"
            className="w-full"
            disabled={!selected}
            onClick={() => {
              if (!selected) return;
              captureEvent("enroll_zip_split_submitted", {
                variant: "sheet",
                postal_code: postalCode ?? null,
                utility: selected.id,
              });
              onSubmit(selected);
            }}
            data-testid="zip-utility-split-sheet-submit"
          >
            View offer
          </Button>
          <EnrollHelpButton eventVariant="split_utility_sheet" />
        </ButtonFooter>
      }
    >
      <UtilityRadioGroup
        name="zip-utility-split-sheet"
        utilities={utilities}
        value={selected}
        onChange={setSelected}
        onClickProvideAddress={onClickProvideAddress}
      />
    </EnrollBottomSheet>
  );
};
